import { useKeyPress } from 'ahooks';
import { useCallback } from 'react';

/**
 * Wrapper around useKeyPress that conditionally disables shortcuts.
 *
 * Automatically prevents default browser behavior when the handler executes.
 *
 * @param key - Key combination to listen for (same format as useKeyPress)
 * @param handler - Callback to execute when key is pressed
 * @param shouldDisable - Function or boolean. If true, handler won't execute.
 *
 * @example
 * ```tsx
 * // Disable when input is focused
 * useSafeKeyPress('k', handleKey, () => isInputFocused());
 *
 * // Disable with multiple conditions
 * useSafeKeyPress('a', handleSelect, () => isViewerOrGuestUser || isInputFocused());
 *
 * // Boolean condition
 * useSafeKeyPress('esc', handleClose, isDisabled);
 * ```
 */
export function useSafeKeyPress(
	key: string | string[],
	handler: (event: KeyboardEvent) => void,
	shouldDisable: boolean | (() => boolean)
) {
	const wrappedHandler = useCallback(
		(event: KeyboardEvent) => {
			const isDisabled =
				typeof shouldDisable === 'function' ? shouldDisable() : shouldDisable;

			if (isDisabled) {
				return;
			}

			// Prevent default browser behavior for shortcuts
			event.preventDefault();
			handler(event);
		},
		[handler, shouldDisable]
	);

	useKeyPress(key, wrappedHandler);
}
